import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, Wand2, X, Loader2, Users } from 'lucide-react';
import { uploadToPath, resolveDownloadUrl } from '../../services/storageService';
import { generateStudioFrame } from '../../services/studioService';
import type { Character } from '../../services/characterService';
import ReferenceUpload from './ReferenceUpload';

interface Props {
  framePath?: string | null;
  uid: string;
  conversationId: string | null;
  /** The cast bible — casting a character sets the shot's start frame to their locked face (consistency). */
  cast?: Character[];
  onFrame: (path: string | null) => void;
}

/**
 * StartFrameMenu — the per-shot START FRAME slot (the image a shot animates FROM). Two ways to set it: ATTACH
 * (upload a reference image — free, just an upload) or GENERATE from a prompt (our image feature makes a Hero
 * Frame — metered, charge-on-success image credits). Shows a live preview once set, with Remove. Monochrome + Lucide.
 */
const StartFrameMenu: React.FC<Props> = ({ framePath, uid, conversationId, cast = [], onFrame }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [prompting, setPrompting] = useState(false);
  const [prompt, setPrompt] = useState('');
  /** An optional image to generate FROM — cleared once the frame it produced is set. */
  const [reference, setReference] = useState<string | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    let alive = true;
    if (!framePath) { setPreviewUrl(null); return; }
    resolveDownloadUrl(framePath).then((u) => { if (alive) setPreviewUrl(u); }).catch(() => { if (alive) setPreviewUrl(null); });
    return () => { alive = false; };
  }, [framePath]);

  const attach = async (file: File) => {
    setBusy(true); setError('');
    try {
      const safe = file.name.replace(/[^\w.-]+/g, '_').slice(-100);
      const path = `chat-attachments/${uid}/${conversationId || 'studio'}/${Date.now()}_${safe}`;
      await uploadToPath(path, file, file.type || 'image/png');
      onFrame(path);
      setOpen(false);
    } catch {
      setError('Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    const p = prompt.trim();
    if (!p) return;
    setBusy(true); setError('');
    try {
      // PROMPT + REFERENCE beats prompt alone (owner law 2026-08-29): an image the creator already has grounds
      // the generation instead of being an either/or alternative to it.
      const { path, needsTopUp, providerDown } = await generateStudioFrame(p, conversationId, reference);
      if (providerDown) { setError('Image generation is temporarily unavailable — try again shortly.'); return; }
      if (needsTopUp) { setError('You are out of credits — top up to generate a frame.'); return; }
      if (path) { onFrame(path); setOpen(false); setPrompting(false); setPrompt(''); }
    } catch {
      setError('Could not generate a frame.');
    } finally {
      setBusy(false);
    }
  };

  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Start frame — click, or drag an image here"
        aria-label="Start frame"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
          if (file) attach(file);
        }}
        className={`flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-lg border border-dashed text-secondary-400 hover:border-secondary-400 ${dragOver ? 'border-secondary-900 dark:border-white bg-secondary-100 dark:bg-secondary-800' : 'border-secondary-300 dark:border-secondary-600'}`}
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : previewUrl ? <img src={previewUrl} alt="" className="h-full w-full object-cover" /> : <><ImageIcon className="h-5 w-5" /><span className="text-[11px] leading-none">Frame</span></>}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-xl border border-secondary-200 bg-white shadow-xl dark:border-secondary-700 dark:bg-secondary-800">
          {!prompting ? (
            <div className="py-1">
              {cast.filter((c) => c.reference_path).length > 0 && (
                <div className="border-b border-secondary-100 dark:border-secondary-700 pb-1">
                  <p className="flex items-center gap-1.5 px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-400"><Users className="h-3 w-3" /> Cast a character</p>
                  {cast.filter((c) => c.reference_path).map((c) => (
                    <button key={c.id} type="button" onClick={() => { onFrame(c.reference_path); setOpen(false); }} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-secondary-900 hover:bg-secondary-100 dark:text-white dark:hover:bg-secondary-700">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-secondary-900 text-[8px] font-semibold text-white dark:bg-white dark:text-secondary-900">{c.name.charAt(0).toUpperCase()}</span> {c.name}
                    </button>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => fileRef.current?.click()} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-secondary-900 hover:bg-secondary-100 dark:text-white dark:hover:bg-secondary-700">
                <Upload className="h-4 w-4 text-secondary-500 dark:text-secondary-300" /> Attach image
              </button>
              <button type="button" onClick={() => setPrompting(true)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-secondary-900 hover:bg-secondary-100 dark:text-white dark:hover:bg-secondary-700">
                <Wand2 className="h-4 w-4 text-secondary-500 dark:text-secondary-300" /> Generate with a prompt
              </button>
              {framePath && (
                <button type="button" onClick={() => { onFrame(null); setOpen(false); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-secondary-900 hover:bg-secondary-100 dark:text-white dark:hover:bg-secondary-700">
                  <X className="h-4 w-4 text-secondary-500 dark:text-secondary-300" /> Remove frame
                </button>
              )}
              {error && <p className="px-3 pb-1 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p>}
            </div>
          ) : (
            <div className="p-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Describe the frame… e.g. a steaming espresso on a wooden counter, warm light"
                className="w-full resize-none rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 px-2.5 py-2 text-xs text-secondary-900 dark:text-white placeholder:text-secondary-400"
              />
              {/* A reference GROUNDS the prompt (owner law 2026-08-29) — it is not the either/or "Attach image"
                  above, which sets the frame outright. Someone who has the shot in mind as a picture should be
                  able to hand it over rather than describe their way back to it. */}
              <div className="mt-1.5 flex items-center gap-1.5">
                <ReferenceUpload
                  path={reference}
                  onChange={setReference}
                  folder={conversationId || 'studio'}
                  label="Generate from an image"
                  disabled={busy}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-end gap-1.5">
                <button type="button" onClick={() => { setPrompting(false); setError(''); }} className="rounded-lg px-2.5 py-1 text-xs text-secondary-500 hover:text-secondary-800 dark:hover:text-white">Cancel</button>
                <button type="button" onClick={generate} disabled={busy || !prompt.trim()} className="inline-flex items-center gap-1 rounded-lg bg-secondary-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-secondary-900">
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Generate
                </button>
              </div>
              {error && <p className="pt-1 text-[11px] text-secondary-500 dark:text-secondary-400">{error}</p>}
            </div>
          )}
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) attach(f); if (fileRef.current) fileRef.current.value = ''; }} />
    </div>
  );
};

export default StartFrameMenu;
